import React, { FC, useState, MouseEvent, useEffect, useCallback, useRef } from 'react'
import { ArrayField, UseFormMethods } from 'react-hook-form'
import capitalize from 'lodash/capitalize'
import get from 'lodash/get'
import maxBy from 'lodash/maxBy'
import moment from 'moment'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledTextArea from 'transfix-form/components/ControlledTextArea'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import ClockIcon from 'transfix-icons/build/ClockIcon'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import EllipsisIcon from 'transfix-icons/build/EllipsisIcon'
import LocationPinIcon from 'transfix-icons/build/LocationPinIcon'
import NumberIcon from 'transfix-icons/build/NumberIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { Card, Typography, CardContent, Hidden, Button as MuiButton } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import { makeStyles, createStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { ControlledFacility } from 'apps/fleet-planner/components/create-load/ControlledFacility'
import { ControlledSelectUnitType } from 'apps/fleet-planner/components/create-load/ControlledSelectUnitType'
import { StopInputContainer } from 'apps/fleet-planner/components/create-load/StopInputContainer'
import { STOP_TYPE } from 'apps/fleet-planner/constants/loads'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import {
  ShipmentStopSchemaType,
  IShipmentStop,
  CreateLoadSchemaType,
} from 'apps/fleet-planner/schema'
import { Checkbox } from 'components/forms'
import { ControlledStopTimes } from './ControlledStopTimes'

type TClassKey = 'root' | 'title' | 'cardHeader' | 'cardContent' | 'cardFooter' | 'inlineLink'

interface IUseStopCardStylesProps {
  isExpanded: boolean
}

const useStopCardStyles = makeStyles(theme =>
  createStyles<TClassKey, IUseStopCardStylesProps>({
    root: {
      border: '1px solid #A9B2BD', // TODO: add theme color
      borderRadius: theme.shape.borderRadius * 2, // 8px
    },
    title: {
      fontFamily: theme.typography.fontFamily,
      fontSize: 20,
      lineHeight: 1,
      color: theme.palette.common.black,
    },
    cardHeader: {
      padding: theme.spacing(3),
      paddingBottom: 0,
    },
    cardContent: {
      padding: theme.spacing(3),
    },
    cardFooter: {
      paddingTop: 0,
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      color: brandColors.coolGray5,
      borderTop: props => (props.isExpanded ? '1px solid #FFFFFF' : '1px solid #A9B2BD'), // TODO: add theme color
    },
    inlineLink: {
      background: 'transparent',
      padding: 0,
      color: '#697684', // TODO: add theme color
      textDecoration: 'underline',
      margin: '0 0.25em',

      '&:hover, &:active, &:focus': {
        background: 'transparent',
        border: 'none',
      },
    },
  })
)

interface IStopCardProps extends Pick<UseFormMethods<CreateLoadSchemaType>, 'register'> {
  fieldIndex: number
  stopField: Partial<ArrayField<IShipmentStop, 'id'>>
  /**
   * @example 1, 2, 3
   */
  mapMarkerValue: number
  /**
   * Triggers a callback which uses the `remove` method
   * from `useFieldArray`
   */
  onRemoveStop: () => void
}

/**
 * Used as an individual Stop in the Create Load form
 */
export const StopCard: FC<IStopCardProps> = ({
  fieldIndex,
  mapMarkerValue,
  onRemoveStop,
  stopField,
}) => {
  const { displayTimezone: displayTimezoneProp = moment.tz.guess(), stopSequence = 1, stopType } =
    stopField || {}
  const { isMobile } = useBreakpointContext()

  const { control, errors, getValues, setValue } = useCreateLoadFormContext()

  const [isExpanded, setExpanded] = useState<boolean>(false)

  const classes = useStopCardStyles({
    isExpanded,
  })

  // Toggles `isExpanded` state to reveal hidden fields
  const clickExpandHandler = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setExpanded(prev => !prev)
  }

  // Gets typed value from `stopField[keyName]`
  function getDefaultValue<K extends keyof ShipmentStopSchemaType>(
    keyName: K
  ): ShipmentStopSchemaType[K] {
    return get(stopField, keyName, '') as ShipmentStopSchemaType[K]
  }

  // Generates an input name, based on `fieldIndex` and `keyName`
  const getInputName = (keyName: keyof ShipmentStopSchemaType): string => {
    return `stops[${fieldIndex}].${keyName}`
  }

  /**
   * Uses the previous stops startAt as default if available
   */
  const getDefaultStartAt = useCallback((): string => {
    let lastStop: IShipmentStop | undefined
    if (stopSequence > 1) {
      const { stops = [] } = getValues()
      const filteredStops = stops.filter(s => s.stopType === stopField.stopType)
      lastStop = maxBy(filteredStops, 'stopSequence')
    }
    return lastStop && lastStop.startAt ? lastStop.startAt : ''
  }, [getValues, stopField.stopType, stopSequence])

  const hasMounted = useRef(false)
  useEffect(() => {
    if (!hasMounted.current) {
      setValue(`stops[${fieldIndex}].endAt`, stopField.endAt)
      hasMounted.current = true
    }
  }, [fieldIndex, setValue, stopField.endAt])

  /**
   * Toggles checkbox to copy commodity/weight/unit info from Pickup 1 stop
   */
  const [isCopiedCommodityInfo, setCopiedCommodityInfo] = useState<boolean>(false)
  const changeCopiedCommodityInfoHandler = ({
    target: { checked },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setCopiedCommodityInfo(checked)

    // Default is set to object with undefined props
    let changes: Partial<Pick<
      ShipmentStopSchemaType,
      'unit' | 'unitQuantity' | 'commodity' | 'weight'
    >> = {}

    // If checked is `true`, get the value. Otherwise, leave all `undefined`
    if (checked) {
      const { stops = [] } = getValues()
      const [firstStop] = stops || []
      changes = { ...firstStop }
    }

    const { commodity, unit, unitQuantity, weight } = changes || {}
    setValue(getInputName('weight'), weight)
    setValue(getInputName('commodity'), commodity)
    setValue(getInputName('unitQuantity'), unitQuantity)
    setValue(getInputName('unit'), unit)
  }

  return (
    <Card className={classes.root} elevation={1}>
      {/* Header */}
      <CardContent className={classes.cardHeader}>
        <Box display='flex' justifyContent='space-between'>
          <Box mr={2}>
            <MapMarker type={getDefaultValue('stopType') as STOP_TYPE} sequence={mapMarkerValue} />
          </Box>
          <Box flexGrow={1}>
            <Typography className={classes.title} color='textSecondary'>
              {`${capitalize(getDefaultValue('stopType'))} ${
                mapMarkerValue > 0 ? mapMarkerValue : ''
              }`}
            </Typography>
          </Box>
          <Box position='relative'>
            <Box position='absolute' right={0} top={0}>
              {stopSequence > 1 && (
                <IconButton
                  color='tertiary'
                  label='Remove Stop'
                  Icon={DeleteIcon}
                  onClick={onRemoveStop}
                  size='large'>
                  Remove Stop
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>

      {/* Main content card section */}
      <CardContent className={classes.cardContent}>
        {/* Facility */}
        <StopInputContainer IconComponent={LocationPinIcon} mb={2}>
          <ControlledFacility
            control={control}
            setValue={setValue}
            namePrefix={`stops[${fieldIndex}]`}
            defaultValue={{}}
            FacilityProps={{
              large: isMobile,
              label: 'Pickup facility',
              dataTest: `facility-${fieldIndex}-autosuggest`,
              required: true,
              errors: {
                city: get(errors, `stops[${fieldIndex}].city.message`, ''),
                stateCode: get(errors, `stops[${fieldIndex}].stateCode.message`, ''),
                locality: get(errors, `stops[${fieldIndex}].address.locality.message`, ''),
                subdivisionCode: get(
                  errors,
                  `stops[${fieldIndex}].address.subdivisionCode.message`,
                  ''
                ),
                facility: get(errors, `stops[${fieldIndex}].facility.name.message`, ''),
              },
            }}
          />
        </StopInputContainer>

        {/* Appointment */}
        <StopInputContainer IconComponent={ClockIcon} hasLabel={false}>
          <ControlledStopTimes
            stopField={stopField}
            fieldIndex={fieldIndex}
            displayTimezone={displayTimezoneProp}
            defaultValues={{
              startAt: getDefaultStartAt(),
            }}
          />
        </StopInputContainer>

        <StopInputContainer IconComponent={NumberIcon} mb={0}>
          <Grid container spacing={2}>
            {/* Appointment Number */}
            <Grid item xs={12} md={6}>
              <DeprecatedControlledTextInput
                debounceMs={150}
                defaultValue={getDefaultValue('appointmentNumber')}
                name={getInputName('appointmentNumber')}
                control={control}
                TextInputProps={{
                  label: 'Appointment Number',
                  large: isMobile,
                  dataTest: `${stopField.stopType}-${fieldIndex}-appointment`,
                }}
              />
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} md={6}>
              <DeprecatedControlledTextInput
                debounceMs={150}
                defaultValue={getDefaultValue('referenceNumber')}
                name={getInputName('referenceNumber')}
                control={control}
                TextInputProps={{
                  label: `${stopType === 'pickup' ? 'Pickup' : 'Delivery'} Number`,
                  large: isMobile,
                  dataTest: `${stopField.stopType}-${fieldIndex}-reference`,
                }}
              />
            </Grid>
          </Grid>
        </StopInputContainer>
      </CardContent>

      {/* Footer */}
      <CardContent className={classes.cardFooter}>
        <Hidden xsUp={isExpanded}>
          <Box pt={2}>
            <StopInputContainer IconComponent={EllipsisIcon} hasLabel={false} mb={0}>
              <Box display='flex' alignItems='center' height='100%' justifyContent='flex-start'>
                <strong>Add</strong>
                <MuiButton
                  data-test={`${stopField.stopType}-${fieldIndex}-add-notes`}
                  variant='text'
                  onClick={clickExpandHandler}
                  className={classes.inlineLink}
                  title={`${stopField.stopType} notes`}>
                  {`${stopField.stopType} notes`}
                </MuiButton>
                <strong>or</strong>
                <MuiButton
                  variant='text'
                  onClick={clickExpandHandler}
                  className={classes.inlineLink}
                  title='commodity details'>
                  commodity details
                </MuiButton>
              </Box>
            </StopInputContainer>
          </Box>
        </Hidden>

        {/* Show when expanded */}
        <Hidden xsUp={!isExpanded}>
          {/* Only show if this is -not- Pickup 1 */}
          {fieldIndex > 0 && (
            <StopInputContainer iconClassName='' hasLabel={false} mb={1}>
              <Checkbox
                dataTest={`copy-commodity-checkbox-${fieldIndex}`}
                label='Same commodity, weight, and quantity as Pickup 1'
                checked={isCopiedCommodityInfo}
                onChange={changeCopiedCommodityInfoHandler}
              />
            </StopInputContainer>
          )}

          {/* Pickup Number / Appointment Number */}
          <StopInputContainer IconComponent={ShipmentsIcon}>
            <Grid container spacing={2}>
              {/* Commodity */}
              <Grid item xs={12}>
                <DeprecatedControlledTextInput
                  debounceMs={150}
                  defaultValue={getDefaultValue('commodity')}
                  name={getInputName('commodity')}
                  control={control}
                  TextInputProps={{
                    label: 'Commodity',
                    large: isMobile,
                    dataTest: `${stopField.stopType}-${fieldIndex}-commodity`,
                    disabled: isCopiedCommodityInfo,
                    clearable: !isCopiedCommodityInfo,
                  }}
                />
              </Grid>

              {/* Weight */}
              <Grid item xs={12} md={4}>
                <DeprecatedControlledTextInput
                  debounceMs={150}
                  defaultValue={getDefaultValue('weight')}
                  name={getInputName('weight')}
                  control={control}
                  TextInputProps={{
                    label: 'Weight (lbs)',
                    large: isMobile,
                    dataTest: `${stopField.stopType}-${fieldIndex}-weight`,
                    disabled: isCopiedCommodityInfo,
                    clearable: !isCopiedCommodityInfo,
                  }}
                />
              </Grid>

              {/* Quantity */}
              <Grid item xs={12} md={4}>
                <DeprecatedControlledTextInput
                  debounceMs={150}
                  defaultValue={getDefaultValue('unitQuantity')}
                  name={getInputName('unitQuantity')}
                  control={control}
                  TextInputProps={{
                    label: 'Quantity',
                    large: isMobile,
                    dataTest: `${stopField.stopType}-${fieldIndex}-unit-quantity`,
                    disabled: isCopiedCommodityInfo,
                    clearable: !isCopiedCommodityInfo,
                  }}
                />
              </Grid>

              {/* Unit Type */}
              <Grid item xs={12} md={4}>
                <ControlledSelectUnitType
                  control={control}
                  name={getInputName('unit')}
                  SelectProps={{
                    large: isMobile,
                    disabled: isCopiedCommodityInfo,
                  }}
                />
              </Grid>

              {/* PO Number */}
              <Grid item xs={12} md={6}>
                <DeprecatedControlledTextInput
                  debounceMs={150}
                  defaultValue={getDefaultValue('poNumber')}
                  name={getInputName('poNumber')}
                  control={control}
                  TextInputProps={{
                    label: 'PO Number',
                    large: isMobile,
                    dataTest: `${stopField.stopType}-${fieldIndex}-po`,
                  }}
                />
              </Grid>

              {/* PO Number */}
              <Grid item xs={12} md={6}>
                <DeprecatedControlledTextInput
                  debounceMs={150}
                  defaultValue={getDefaultValue('bolNumber')}
                  name={getInputName('bolNumber')}
                  control={control}
                  TextInputProps={{
                    label: 'BOL #',
                    large: isMobile,
                    dataTest: `${stopField.stopType}-${fieldIndex}-bol`,
                  }}
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <ControlledTextArea
                  defaultValue={getDefaultValue('instructions')}
                  name={getInputName('instructions')}
                  control={control}
                  TextAreaProps={{
                    placeholder: 'Write something...',
                    label: `${capitalize(stopField.stopType)} instructions`,
                    size: 'large',
                    dataTest: `${stopField.stopType}-${fieldIndex}-instructions`,
                  }}
                />
              </Grid>
            </Grid>
          </StopInputContainer>
        </Hidden>
      </CardContent>
      <ControlledHiddenInput
        control={control}
        name={getInputName('stopSequence')}
        defaultValue={getDefaultValue('stopSequence') ?? ''}
      />
      <ControlledHiddenInput
        control={control}
        name={getInputName('stopType')}
        defaultValue={getDefaultValue('stopType') ?? ''}
      />
    </Card>
  )
}
